<template>
    <div class="agenda-body">
        <div class="agenda-header">
            <span class="agenda-title">{{ $t('titles.agenda') }}</span>
        </div>

        <agenda-mini-calendar :value="selectedDate" :event-dates="eventDates" @select="onSelectDate" />

        <div class="day-summary">
            <div class="day-summary-trigger" @click="expanded = !expanded">
                <div class="day-summary-left">
                    <span class="day-title">{{ dayTitle }}</span>
                    <span v-if="selectedDayRoutines.length" class="day-badge">{{ selectedDayRoutines.length }}</span>
                </div>
                <v-icon size="16" :class="['day-chevron', { open: expanded }]">mdi-chevron-down</v-icon>
            </div>
            <v-expand-transition>
                <div v-show="expanded" class="events-list" :class="{ 'events-list--scroll': hasEventsScroll }">
                    <skeleton-loader v-if="loading" type="agenda-cards" :count="3" />
                    <v-virtual-scroll v-else-if="selectedDayRoutines.length" class="events-scroll"
                        :items="selectedDayRoutines" :item-height="itemHeight" :height="virtualHeight" :bench="5">
                        <template #default="{ item }">
                            <div class="event-item">
                                <agenda-event-card :routine="item" :student-name="getStudentName(item.studentId)"
                                    :avatar="getStudentAvatar(item.studentId)" :readonly="readonly"
                                    @click="!readonly && onEdit(item)" @click:edit="onEdit(item)"
                                    @click:copy="showCopyRoutine(item)" @click:delete="showDeleteRoutine(item)" />
                            </div>
                        </template>
                    </v-virtual-scroll>
                    <div v-else class="no-events">{{ $t('admin.no_assigned_routines') }}</div>
                </div>
            </v-expand-transition>
        </div>

        <agenda-videos v-if="!readonly" />

        <template v-if="!readonly">
            <copy-routine-dialog :coach-id="activeCoachId" @success="onCopySuccess" :students-options="students"
                :groups-options="groups" :teams-options="teams" :current-selection="currentSelection" ref="copy"
                auto-date />

            <add-competence-dialog :current-selection="currentSelection" collection="assignedRoutines"
                :coach-id="activeCoachId" ref="competence" @saved="onSaveCompetence" :routine-number="routineNumber" />

            <kimsa-confirm :title="$t('routines.delete')" :content="$t('routines.confirm_delete')"
                @confirm="onDeleteRoutine" :close-on-confirm="false" :loading="loadingDelete" alert-mode ref="confirm" />

            <routine-editor ref="editor" :coach-id="activeCoachId" @success="onEditSuccess" />
        </template>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { db, getAllByIds } from "@/utils/Firebase";
import { uniqueDayRoutine } from "@/utils/Exercises";
import AgendaMiniCalendar from "@/components/coach/agenda/AgendaMiniCalendar";
import AgendaEventCard from "@/components/coach/agenda/AgendaEventCard";
import AgendaVideos from "@/components/coach/agenda/AgendaVideos";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import CopyRoutineDialog from "@/components/common/routines/CopyRoutineDialog";
import AddCompetenceDialog from "@/components/coach/competences/AddCompetenceDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import RoutineEditor from "@/components/coach/routines/RoutineEditor";

const ITEM_HEIGHT = 80
const MAX_LIST_HEIGHT = 480

export default {
    name: "AgendaPanelBody",
    components: {
        AgendaMiniCalendar,
        AgendaEventCard,
        AgendaVideos,
        SkeletonLoader,
        CopyRoutineDialog,
        AddCompetenceDialog,
        KimsaConfirm,
        RoutineEditor,
    },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedDate: this.$moment().format('YYYY-MM-DD'),
            expanded: true,
            loading: false,
            loadingDelete: false,
            students: [],
            loadedDates: new Set(),
            currentSelection: null,
            currentRoutine: null,
        }
    },
    computed: {
        ...mapState(['coachAssignedRoutines', 'groups', 'teams']),
        activeCoachId(vm = this) {
            return vm.coachId || vm.currentUser?.uid || null
        },
        eventDates(vm = this) {
            return [...new Set(vm.coachAssignedRoutines.map(r => r?.rawDate?.full).filter(Boolean))]
        },
        selectedDayRoutines(vm = this) {
            const routines = vm.coachAssignedRoutines.filter(r => r?.rawDate?.full === vm.selectedDate)
            return uniqueDayRoutine(routines).sort((a, b) => {
                const nameA = a?.name?.toLowerCase() || ""
                const nameB = b?.name?.toLowerCase() || ""
                return nameA.localeCompare(nameB)
            })
        },
        dayTitle(vm = this) {
            return vm.$moment(vm.selectedDate).format(vm.$t('common.date_format_day_month'))
        },
        routineNumber(vm = this) {
            return vm.currentRoutine?.routineNumber || 1
        },
        itemHeight() {
            return ITEM_HEIGHT
        },
        hasEventsScroll(vm = this) {
            return (vm.selectedDayRoutines.length * ITEM_HEIGHT) > MAX_LIST_HEIGHT
        },
        virtualHeight(vm = this) {
            return Math.min(vm.selectedDayRoutines.length * ITEM_HEIGHT, MAX_LIST_HEIGHT)
        },
    },
    async mounted() {
        const vm = this
        vm.$store.commit('resetCoachAssignedRoutines')
        await vm.loadDay(vm.selectedDate)
    },
    methods: {
        ...mapActions(['fetchCoachAssignedRoutinesOfDay']),
        async loadDay(date) {
            const vm = this
            if (vm.loadedDates.has(date)) return
            await vm.withLoading(async () => {
                await vm.fetchCoachAssignedRoutinesOfDay({ coachId: vm.activeCoachId, date })
                vm.loadedDates.add(date)
                await vm.resolveStudentNames()
            })
        },
        reloadDay(date) {
            const vm = this
            vm.$store.commit('removeCoachAssignedRoutines', date)
            vm.loadedDates.delete(date)
            vm.loadDay(date)
        },
        onSelectDate(date) {
            const vm = this
            vm.selectedDate = date
            vm.expanded = true
            vm.loadDay(date)
        },
        async resolveStudentNames() {
            const vm = this
            const ids = (vm.coachAssignedRoutines || []).map(r => r.studentId).filter(Boolean)
            const known = new Set(vm.students.map(s => s.objectID))
            const missing = [...new Set(ids)].filter(id => !known.has(id))
            if (!missing.length) return
            const docs = await getAllByIds('users', missing)
            if (!docs.length) return
            vm.students = [...vm.students, ...docs.map(d => ({ ...d, name: d.name || d.email }))]
        },
        async ensureStudent(studentId) {
            const vm = this
            if (!studentId) return null
            let student = vm.students.find(s => s.objectID === studentId)
            if (student) return student
            const [doc] = await getAllByIds('users', [studentId])
            if (!doc) return null
            student = { ...doc, name: doc.name || doc.email }
            vm.students = [...vm.students, student]
            return student
        },
        getStudentName(studentId) {
            const vm = this
            return vm.students.find(s => s.objectID === studentId)?.name || '...'
        },
        getStudentAvatar(studentId) {
            const vm = this
            return vm.students.find(s => s.objectID === studentId)?.avatar || null
        },
        onEdit(routine) {
            const vm = this
            if (routine?.type === 'competence') return vm.showEditCompetence(routine)
            vm.$refs.editor.showEditAssigned(routine)
        },
        onEditSuccess() {
            this.reloadDay(this.selectedDate)
        },
        async showCopyRoutine(routine) {
            const vm = this
            vm.currentSelection = await vm.ensureStudent(routine.studentId)
            vm.$refs.copy.show({ date: routine.rawDate.full, routineId: routine.routineId, assignedRoutineId: routine.id })
        },
        async showEditCompetence(routine) {
            const vm = this
            vm.currentRoutine = routine
            vm.currentSelection = await vm.ensureStudent(routine.studentId)
            vm.$refs.competence.show(routine.rawDate.full, routine)
        },
        onCopySuccess() {
            this.reloadDay(this.selectedDate)
        },
        onSaveCompetence() {
            const vm = this
            vm.currentSelection = null
            vm.currentRoutine = null
            vm.reloadDay(vm.selectedDate)
        },
        showDeleteRoutine(routine) {
            const vm = this
            if (!routine?.id) return vm.toast(vm.$t('routines.error_with_routine'), 'error')
            vm.currentRoutine = routine
            vm.$refs.confirm.show()
        },
        async onDeleteRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                const deletedId = vm.currentRoutine.id
                await db.collection('assignedRoutines').doc(deletedId).delete()
                vm.$store.commit('setCoachAssignedRoutines', vm.coachAssignedRoutines.filter(r => r.id !== deletedId))
                vm.$refs.confirm?.hide()
                vm.currentRoutine = null
                vm.toast(vm.$t('routines.deleted'))
            }, 'loadingDelete')
        },
    },
    watch: {
        coachId() {
            const vm = this
            vm.$store.commit('resetCoachAssignedRoutines')
            vm.loadedDates = new Set()
            vm.students = []
            vm.loadDay(vm.selectedDate)
        },
    },
}
</script>

<style scoped>
.agenda-header {
    height: 60px;
    border-bottom: 1px solid var(--v-outline-base);
    padding: 0 20px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.agenda-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.day-summary {
    margin: 0 20px;
}

.day-summary-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-top: 1px solid var(--v-outline-base);
    cursor: pointer;
    user-select: none;
}

.day-summary-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.day-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.day-badge {
    background: rgba(102, 204, 204, 0.12);
    border: 1px solid rgba(102, 204, 204, 0.3);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-accent-base);
}

.day-chevron {
    transition: transform 0.2s;
}

.day-chevron.open {
    transform: rotate(180deg);
}

.events-list {
    padding-bottom: 12px;
}

.events-scroll::-webkit-scrollbar {
    width: 5px;
}

.events-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.events-scroll::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 3px;
}

.event-item {
    padding-bottom: 8px;
}

.events-list--scroll .event-item {
    padding-right: 8px;
}

.no-events {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 4px 0 8px;
}
</style>
